/**
 * Subscription Editor Component
 *
 * Modal for editing user subscriptions
 * Allows admins to modify tier, set expiration, and provide reason
 *
 * T084: Create subscription-editor.ts - Modify tier, set expiration, reason input
 * T087: Wire up updateUserSubscription callable
 */

import { functions } from '@/core/firebase'
import { httpsCallable } from 'firebase/functions'

/**
 * User data structure (from user-list.ts)
 */
interface UserData {
  uid: string
  email: string | null
  displayName: string | null
  planType: string
  [key: string]: unknown
}

interface UpdateSubscriptionInput {
  targetUserId: string
  targetUserEmail?: string
  newPlanType: 'free' | 'standard' | 'enterprise'
  expiresAt?: string
  reason: string
  branchIds?: string[]
}

interface UpdateSubscriptionResponse {
  success: boolean
  subscription: Record<string, unknown>
  message: string
}

/**
 * Show subscription editor modal
 *
 * T084: Create modal with tier selection, expiration date, and reason input
 */
export function showSubscriptionEditor(user: UserData): void {
  console.log('[Subscription Editor] Opening editor for user:', user.uid)

  const modal = document.createElement('div')
  modal.className = 'fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50'
  modal.style.backdropFilter = 'blur(4px)'

  modal.innerHTML = `
    <div class="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 overflow-hidden">
      <div class="p-6">
        <!-- Header -->
        <div class="flex items-center justify-between mb-4">
          <h2 class="text-2xl font-bold text-gray-900">Edit Subscription</h2>
          <button
            id="close-editor-modal"
            class="text-gray-400 hover:text-gray-600"
          >
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>

        <!-- Body -->
        <div class="space-y-4">
          <!-- User Info -->
          <div class="bg-gray-50 rounded-lg p-3">
            <div class="text-sm text-gray-600">User</div>
            <div class="text-sm font-medium text-gray-900">${user.email || 'No email'}</div>
            <div class="text-xs text-gray-500 font-mono">${user.uid}</div>
          </div>

          <!-- Current Plan -->
          <div>
            <div class="text-sm text-gray-600">Current Plan</div>
            <div class="text-sm font-medium text-gray-900 capitalize">${user.planType}</div>
          </div>

          <!-- New Plan Type -->
          <div>
            <label for="new-plan-type" class="block text-sm font-medium text-gray-700 mb-1">
              New Plan Type *
            </label>
            <select
              id="new-plan-type"
              class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              required
            >
              <option value="free" ${user.planType === 'free' ? 'selected' : ''}>Free</option>
              <option value="standard" ${user.planType === 'standard' ? 'selected' : ''}>Standard</option>
              <option value="enterprise" ${user.planType === 'enterprise' ? 'selected' : ''}>Enterprise</option>
            </select>
          </div>

          <!-- Expiration Date (Optional) -->
          <div>
            <label for="expiration-date" class="block text-sm font-medium text-gray-700 mb-1">
              Expiration Date (Optional)
            </label>
            <input
              type="datetime-local"
              id="expiration-date"
              class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            />
            <p class="mt-1 text-xs text-gray-500">Leave empty for no expiration</p>
          </div>

          <!-- Reason -->
          <div>
            <label for="update-reason" class="block text-sm font-medium text-gray-700 mb-1">
              Reason for Update *
            </label>
            <textarea
              id="update-reason"
              rows="3"
              placeholder="e.g., Customer request, promotion, support issue resolution"
              class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              required
            ></textarea>
            <p class="mt-1 text-xs text-gray-500">This will be logged in the audit trail</p>
          </div>

          <!-- Error Message -->
          <div id="editor-error" class="hidden p-3 bg-red-50 border border-red-200 rounded-lg">
            <p class="text-sm text-red-600"></p>
          </div>

          <!-- Success Message -->
          <div id="editor-success" class="hidden p-3 bg-green-50 border border-green-200 rounded-lg">
            <p class="text-sm text-green-600">Subscription updated successfully!</p>
          </div>
        </div>

        <!-- Actions -->
        <div class="mt-6 flex gap-3">
          <button
            id="save-subscription-btn"
            class="flex-1 bg-indigo-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Save Changes
          </button>
          <button
            id="cancel-editor-btn"
            class="flex-1 bg-gray-100 text-gray-700 px-4 py-2 rounded-lg font-medium hover:bg-gray-200 transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  `

  document.body.appendChild(modal)

  // Get DOM elements
  const closeBtn = modal.querySelector('#close-editor-modal')
  const saveBtn = modal.querySelector('#save-subscription-btn') as HTMLButtonElement
  const cancelBtn = modal.querySelector('#cancel-editor-btn')
  const planTypeSelect = modal.querySelector('#new-plan-type') as HTMLSelectElement
  const expirationInput = modal.querySelector('#expiration-date') as HTMLInputElement
  const reasonTextarea = modal.querySelector('#update-reason') as HTMLTextAreaElement
  const errorDiv = modal.querySelector('#editor-error') as HTMLElement
  const errorText = errorDiv?.querySelector('p') as HTMLElement
  const successDiv = modal.querySelector('#editor-success') as HTMLElement

  // Close modal function
  const closeModal = () => {
    modal.remove()
  }

  // Event listeners
  closeBtn?.addEventListener('click', closeModal)
  cancelBtn?.addEventListener('click', closeModal)
  modal.addEventListener('click', (e) => {
    if (e.target === modal) {
      closeModal()
    }
  })

  // Save subscription changes
  saveBtn?.addEventListener('click', async () => {
    const newPlanType = planTypeSelect.value as 'free' | 'standard' | 'enterprise'
    const expiresAt = expirationInput.value || undefined
    const reason = reasonTextarea.value.trim()

    // Validation
    if (!reason) {
      errorDiv.classList.remove('hidden')
      errorText.textContent = 'Please provide a reason for this update'
      return
    }

    if (newPlanType === user.planType && !expiresAt) {
      errorDiv.classList.remove('hidden')
      errorText.textContent = 'No changes detected. Please modify plan type or set expiration date.'
      return
    }

    // Hide errors
    errorDiv.classList.add('hidden')
    successDiv.classList.add('hidden')

    // Disable button and show loading state
    saveBtn.disabled = true
    saveBtn.textContent = 'Saving...'

    try {
      // T087: Call updateUserSubscription callable
      const updateSubscriptionCallable = httpsCallable<
        UpdateSubscriptionInput,
        UpdateSubscriptionResponse
      >(functions, 'updateUserSubscription')

      const input: UpdateSubscriptionInput = {
        targetUserId: user.uid,
        targetUserEmail: user.email || undefined,
        newPlanType,
        expiresAt: expiresAt ? new Date(expiresAt).toISOString() : undefined,
        reason,
      }

      console.log('[Subscription Editor] Updating subscription:', input)

      const result = await updateSubscriptionCallable(input)

      console.log('[Subscription Editor] Update successful:', result.data)

      // Show success message
      successDiv.classList.remove('hidden')

      // Close modal and refresh page after short delay
      setTimeout(() => {
        closeModal()
        window.location.reload()
      }, 1500)
    } catch (error: unknown) {
      console.error('[Subscription Editor] Update failed:', error)
      errorDiv.classList.remove('hidden')
      errorText.textContent
        = (error as Error)?.message || 'Failed to update subscription. Please try again.'
      saveBtn.disabled = false
      saveBtn.textContent = 'Save Changes'
    }
  })
}

/**
 * Initialize subscription editor (for main admin panel)
 */
export function initializeSubscriptionEditor(): void {
  console.log('[Subscription Editor] Initialized')
  // No initialization needed - editor is shown on demand via showSubscriptionEditor()
}
